import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { overallPerformanceActions } from '../../../modules/reducer/overallPerformanceSlice';
import { overallPerformanceSubBranchActions } from '../../../modules/reducer/overallPerformanceSubBranch';
import { TransformDate } from '../../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import {
  OVERALL_PERFORMANCE,
  OVERALL_PERFORMANCE_SUB_BRANCH,
  TIME_PERIOD,
  WEEK_SUBSTRACTION_VALUE,
} from '../../../constants/datePickerConstants';
import {
  isStoreValid,
  getFirstSortedValueByRegion,
  checkAndUpdateStoreId,
  formatRegionOptions,
  currentYearAndQuarterFun,
  removeFiltersByKeys,
  getFilteredStoreOptions,
} from '../../../constants/utils';

import { useStoreFilters } from './useStoreFilters';
import { useRegionFilters } from './useRegionFilters';
import { usePerformanceQueries } from './usePerformanceQueries';
import { checkForGlobalObject } from '../services/overallPerformanceServices';
import { useLocation } from 'react-router';
import { OPPS_DATA, SPENDS_DATA } from '../../spendAndOpportunitiesScreen/constants';

export function useOverallPerformanceData({ subBranchDashboard = false }) {
  // Redux state
  const dispatch = useDispatch();
  const location = useLocation();
  const { userStores, userData } = useSelector((state) => state.auth);
  const { transformedData, transformedDataWithDate } = useSelector(
    selectors.datePickerCombinedData
  );

  // Get the correct state and actions based on subBranchDashboard flag
  const regularStateData = useSelector((state) => state.overallPerformance);
  const subBranchStateData = useSelector((state) => state.overallPerformanceSubBranch);
  // Get date picker data for graphs
  const currentYear = userData?.data?.current_year;

  // Conditionally select the appropriate state data
  const {
    overallPerformanceRange,
    compareOverallPerformanceRangeTableOne,
    compareOverallPerformanceRangeYTD,
    overallPerformanceYTDRange,
  } = subBranchDashboard
    ? {
        overallPerformanceRange: subBranchStateData.overallPerformanceSubBranchRange,
        compareOverallPerformanceRangeTableOne:
          subBranchStateData.compareOverallPerformanceSubBranchRangeTableOne,
        compareOverallPerformanceRangeYTD:
          subBranchStateData.compareOverallPerformanceSubBranchRangeYTD,
        overallPerformanceYTDRange: subBranchStateData.overallPerformanceYTDSubBranchRange,
      }
    : regularStateData;

  // Get table IDs from user data - memoize to prevent recalculation
  const tableId = useMemo(
    () =>
      userData?.data?.data_sources?.find((data) => data?.name === 'overall_performance')
        ?.identifier,
    [userData?.data?.data_sources]
  );

  const localSpendTableId = useMemo(
    () =>
      userData?.data?.data_sources?.find(
        (data) => data?.name === 'vw_overall_performance_tab_localbranch'
      )?.identifier,
    [userData?.data?.data_sources]
  );

  const options = useMemo(
    () =>
      getFilteredStoreOptions(userStores, userData?.data, subBranchDashboard, location.pathname),
    [userStores, userData?.data, subBranchDashboard, location.pathname]
  );
  // Helper function to get initial store ID value
  const getInitialStoreId = useCallback(() => {
    // If in sub-branch dashboard mode, return all available options
    if (subBranchDashboard) {
      return options
        ?.map((store) => store?.value)
        ?.filter((value) => value != null && value !== 'none');
    }

    if (userData?.data?.is_gale_admin) {
      return [1];
    } else if (!userData?.data?.is_gale_admin) {
      return getFirstSortedValueByRegion(userData?.data?.region, options);
    } else {
      return options
        ?.map((store) => store?.value)
        ?.filter((value) => value != null && value !== 1 && value !== 'none');
    }
  }, [userData?.data?.is_gale_admin, userData?.data?.region, options]);

  // Store and region state
  const [storeId, setStoreId] = useState(getInitialStoreId());
  const [regionId, setRegionId] = useState();
  const [regionBranches, setRegionBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionTableIds, setRegionTableIds] = useState({});
  const [regionArray, setRegionArray] = useState([]);

  // UI state
  const componentType = subBranchDashboard ? OVERALL_PERFORMANCE_SUB_BRANCH : OVERALL_PERFORMANCE;
  const [defaultFilter, setDefaultFilter] = useState(TIME_PERIOD);
  const [spendToggleState, setSpendToggleState] = useState(false);
  const [showSpendToggle, setShowSpendToggle] = useState(false);
  const [showSelectedRegion, setShowSelectedRegion] = useState(false);
  const [defaultRegionBoolean, setDefaultRegionBoolean] = useState(false);
  const [resetRegion, setResetRegion] = useState(false);
  const [resetStore, setResetStore] = useState(false);

  const storeArray = useMemo(
    () => userData?.data?.stores_data?.map((store) => store.branch_number) || [],
    [userData?.data?.stores_data]
  );

  // Date range filters using custom hooks
  const { currentDateFilters, comparePeriodFilters, compareYtdFilters, currentYtdFilters } =
    useStoreFilters({
      storeId,
      storeArray,
      dateRanges: {
        overallPerformanceRange,
        compareOverallPerformanceRangeTableOne,
        compareOverallPerformanceRangeYTD,
        overallPerformanceYTDRange,
      },
      checkForGlobalObject,
    });

  const {
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
    compareYtdFiltersForRegion,
    currentYtdFiltersForRegion,
  } = useRegionFilters({
    regionId,
    regionArray,
    dateRanges: {
      overallPerformanceRange,
      compareOverallPerformanceRangeTableOne,
      compareOverallPerformanceRangeYTD,
      overallPerformanceYTDRange,
    },
    showSelectedRegion,
    defaultRegionBoolean,
    checkForGlobalObject,
  });

  // API queries using custom hook
  const {
    // Table One - Period Comparison
    tableOneData,
    fetchTableOne,
    isTableOneLoading,
    tableOneError,

    // Table Two - YTD Comparison
    tableTwoData,
    fetchTableTwo,
    isTableTwoLoading,
    tableTwoError,

    // Table Three - Region Comparison
    tableThreeData,
    fetchTableThree,
    isTableThreeLoading,
    tableThreeError,

    // Region-specific tables
    fetchRegionTableOne,

    fetchRegionTableTwo,

    // Combined loading state
    isAnyQueryLoading,
  } = usePerformanceQueries({ subBranchDashboard });

  // With this simplified condition
  const showTableThree = useMemo(() => {
    return (
      tableThreeData?.length > 0 &&
      storeId?.length > 0 &&
      !storeId?.includes(1) &&
      !subBranchDashboard
    );
  }, [tableThreeData, storeId]);

  // Memoize the graph visibility conditions
  const showGraphs = useMemo(
    () => ({
      base: Boolean(currentYear && transformedDataWithDate?.length) && storeId?.length === 1,
      percentage: Boolean(
        currentYear && transformedDataWithDate?.length && !spendToggleState && storeId?.length === 1
      ),
    }),
    [currentYear, transformedDataWithDate?.length, spendToggleState, isAnyQueryLoading]
  );

  // Memoize graph props for opportunities
  const oppsGraphProps = useMemo(
    () => ({
      selectedStore: storeId?.[0],
      currentYear,
      totalYears: transformedDataWithDate?.length,
      userStores,
      dataType: OPPS_DATA,
      spendToggleState,
    }),
    [storeId, currentYear, transformedDataWithDate?.length, userStores, spendToggleState]
  );

  // Memoize graph props for spends
  const spendsGraphProps = useMemo(
    () => ({
      selectedStore: storeId?.[0],
      currentYear,
      totalYears: transformedDataWithDate?.length,
      userStores,
      dataType: SPENDS_DATA,
      spendToggleState,
    }),
    [storeId, currentYear, transformedDataWithDate?.length, userStores, spendToggleState]
  );

  // Region options
  useEffect(() => {
    if (userData?.data?.region) {
      setRegionTableIds(
        userData && Object.entries(userData?.data?.region)?.map(([id, name]) => ({ id, name }))
      );
    }
  }, [userData]);

  useEffect(() => {
    if (regionTableIds && Object.keys(regionTableIds).length > 0) {
      setRegions(regionTableIds);
      const regionArr = regions.map((iter) => iter.id);
      setRegionArray(regionArr.sort());
    }
  }, [regionTableIds, regions]);

  const regionOptions = useMemo(
    () => formatRegionOptions(regions, userData?.data?.is_gale_admin),
    [regions, userData?.data?.is_gale_admin]
  );

  // Helper to dispatch actions based on dashboard type
  const dispatchAction = useCallback(
    (regularAction, subBranchAction, payload) => {
      if (subBranchDashboard) {
        dispatch(subBranchAction(payload));
      } else {
        dispatch(regularAction(payload));
      }
    },
    [dispatch, subBranchDashboard]
  );

  // Date range setup - convert to memoized callback
  const timePeriodRangeHandler = useCallback(() => {
    const allValuesAreNull = checkForGlobalObject(overallPerformanceRange);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (allValuesAreNull && obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
      const quarterValue =
        transformedDataWithDate?.length &&
        TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
          transformedDataWithDate,
          previousWeekExtractor,
          WEEK_SUBSTRACTION_VALUE
        );

      // Use the appropriate action based on dashboard type
      dispatchAction(
        overallPerformanceActions.setOverallPerformanceRange,
        overallPerformanceSubBranchActions.setOverallPerformanceRange,
        quarterValue
      );

      const value = `w${quarterValue?.start_week}, ${quarterValue?.start_year} - w${quarterValue?.end_week}, ${quarterValue?.end_year}`;
      setDefaultFilter(value);
    }
  }, [transformedDataWithDate, overallPerformanceRange, dispatchAction]);

  const timePeriodYTDRange = useCallback(() => {
    if (transformedDataWithDate?.length > 0) {
      const ytd = TransformDate.quickFilters.YEAR_TO_DATE(
        transformedDataWithDate,
        previousWeekExtractor
      );

      // Use the appropriate action based on dashboard type
      dispatchAction(
        overallPerformanceActions.setOverallPerformanceYTDRange,
        overallPerformanceSubBranchActions.setOverallPerformanceYTDRange,
        ytd
      );
    }
  }, [transformedDataWithDate, dispatchAction]);

  const comparisonPeriodYTD = useCallback(() => {
    const allValuesAreNull = checkForGlobalObject(overallPerformanceYTDRange);
    if (!allValuesAreNull) {
      const previousPeriod = TransformDate.comparisonPeriod.previousPeriod(
        overallPerformanceYTDRange
      );

      // Use the appropriate action based on dashboard type
      dispatchAction(
        overallPerformanceActions.setCompareOverallPerformanceRangeYTD,
        overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeYTD,
        previousPeriod
      );
    }
  }, [overallPerformanceYTDRange, dispatchAction]);

  const comparisonPeriodRangeHandler = useCallback(() => {
    const allValuesAreNullForCurr = checkForGlobalObject(overallPerformanceRange);
    if (!allValuesAreNullForCurr) {
      const range = TransformDate.comparisonPeriod.previousFilter(overallPerformanceRange);

      // Use the appropriate action based on dashboard type
      dispatchAction(
        overallPerformanceActions.setCompareOverallPerformanceRangeForTableOne,
        overallPerformanceSubBranchActions.setCompareOverallPerformanceRangeForTableOne,
        range
      );
    }
  }, [overallPerformanceRange, dispatchAction]);

  // Initialize date ranges - reduce dependencies to prevent unnecessary runs
  useEffect(() => {
    if (transformedDataWithDate?.length > 0) {
      timePeriodRangeHandler();
      timePeriodYTDRange();
    }
  }, [transformedDataWithDate, timePeriodRangeHandler, timePeriodYTDRange]);

  useEffect(() => {
    comparisonPeriodYTD();
    comparisonPeriodRangeHandler();
  }, [
    overallPerformanceYTDRange,
    overallPerformanceRange,
    comparisonPeriodYTD,
    comparisonPeriodRangeHandler,
  ]);

  // Quarter and year setup
  const quarterAndYearValueHandler = useCallback(() => {
    if (transformedDataWithDate?.length > 0) {
      const currentYearAndQuarter = currentYearAndQuarterFun(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE,
        TransformDate
      );

      // Use the appropriate actions based on dashboard type
      dispatchAction(
        overallPerformanceActions.setCurrentQuarter,
        overallPerformanceSubBranchActions.setCurrentQuarter,
        currentYearAndQuarter?.currentQuarterValue
      );

      dispatchAction(
        overallPerformanceActions.setCurrentYear,
        overallPerformanceSubBranchActions.setCurrentYear,
        currentYearAndQuarter?.currentYearBasedOnWeek
      );
    }
  }, [transformedDataWithDate, dispatchAction]);

  useEffect(() => {
    if (transformedDataWithDate?.length > 0) {
      quarterAndYearValueHandler();
    }
  }, [transformedDataWithDate, quarterAndYearValueHandler]);

  const compareQuarterAndYearValueHandlerForTableOne = useCallback(() => {
    const allValuesAreNullForCurr = checkForGlobalObject(overallPerformanceRange);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (!allValuesAreNullForCurr && obj && Object.keys(obj).length) {
      const year = obj.year - 1;
      const week = overallPerformanceRange?.start_week;
      const previousValue =
        transformedData.length && TransformDate.findQuarter(year, week, transformedData);

      // Use the appropriate actions based on dashboard type
      dispatchAction(
        overallPerformanceActions.setCompareSelectedQuarter,
        overallPerformanceSubBranchActions.setCompareSelectedQuarter,
        previousValue
      );

      if (obj.quarter === 'Q1') {
        dispatchAction(
          overallPerformanceActions.setCompareSelectedYear,
          overallPerformanceSubBranchActions.setCompareSelectedYear,
          year - 1
        );
      } else {
        dispatchAction(
          overallPerformanceActions.setCompareSelectedYear,
          overallPerformanceSubBranchActions.setCompareSelectedYear,
          year
        );
      }
    }
  }, [overallPerformanceRange, transformedDataWithDate, transformedData, dispatchAction]);

  useEffect(() => {
    if (transformedDataWithDate?.length > 0 && overallPerformanceRange) {
      compareQuarterAndYearValueHandlerForTableOne();
    }
  }, [
    overallPerformanceRange,
    transformedDataWithDate,
    compareQuarterAndYearValueHandlerForTableOne,
  ]);

  // Filter stores by region - convert to callback to prevent recreation
  const filterStoresByRegion = useCallback((inputArray, regionCode) => {
    const array = inputArray
      ? inputArray.filter((store) => store.region === parseInt(regionCode))
      : [];
    setRegionBranches(array);
  }, []);

  // Branch and region selection handlers
  const handleStoreIdChange = useCallback(
    (store) => {
      setStoreId(checkAndUpdateStoreId(store, storeId));
      setDefaultRegionBoolean(false);

      if (store?.length === 0 && subBranchDashboard) {
        setStoreId([]);
      }
      if (store?.length === 0 && !subBranchDashboard) {
        const selectedRegion =
          regionId || (regionOptions?.length > 0 ? regionOptions[0]?.value : undefined);
        setRegionId(selectedRegion);
        !selectedRegion && userData?.data?.is_gale_admin && setStoreId([1]);
        setShowSelectedRegion((prev) => !prev);
      }
    },
    [storeId, regionId, regionOptions, userData?.data?.is_gale_admin]
  );

  const handleRegionIdChange = useCallback(
    (region) => {
      if (region) {
        filterStoresByRegion(options, region);
        setStoreId([]);
        setResetStore(true);
        setRegionId(region);
      } else {
        setRegionBranches([]);
        setRegionId(region);
        setStoreId([1]);
      }
    },
    [options, filterStoresByRegion]
  );

  const handleResetForRegion = useCallback(() => {
    setResetStore(false);
    setResetRegion(true);
  }, []);

  const handleResetForStore = useCallback(() => {
    setResetRegion(false);
    setResetStore(true);
  }, []);

  // CRITICAL FIX: Use refs instead of state for filter hash management
  const filtersHashRef = useRef('');
  const apiCallInProgressRef = useRef(false);

  // Create a hash of the current filter state to prevent duplicate API calls
  const getUpdatedFiltersHash = useCallback(() => {
    return JSON.stringify({
      storeId,
      regionId,
      currentDateFilters,
      comparePeriodFilters,
      currentYtdFilters,
      compareYtdFilters,
      spendToggleState,
      currentDateFiltersForRegion,
      comparePeriodFiltersForRegion,
      currentYtdFiltersForRegion,
      compareYtdFiltersForRegion,
      subBranchDashboard,
    });
  }, [
    storeId,
    regionId,
    currentDateFilters,
    comparePeriodFilters,
    currentYtdFilters,
    compareYtdFilters,
    spendToggleState,
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
    currentYtdFiltersForRegion,
    compareYtdFiltersForRegion,
    subBranchDashboard,
  ]);

  // Fetch data based on branch selection - critical fix to prevent multiple API calls
  useEffect(() => {
    // Don't run if we don't have the necessary data yet
    if (!tableId) return;

    // Get current filters hash
    const currentFiltersHash = getUpdatedFiltersHash();

    // Skip if filters haven't changed or API call already in progress
    if (currentFiltersHash === filtersHashRef.current || apiCallInProgressRef.current) {
      return;
    }

    // Update hash and set API call in progress
    filtersHashRef.current = currentFiltersHash;
    apiCallInProgressRef.current = true;

    // Small debounce to avoid rapid consecutive API calls
    const debounceApiCall = setTimeout(() => {
      // For store-specific data
      if (
        currentDateFilters?.length > 0 &&
        comparePeriodFilters?.length > 0 &&
        currentYtdFilters?.length > 0 &&
        compareYtdFilters?.length > 0 &&
        storeId?.length > 0
      ) {
        // Show spend toggle based on store selection
        setShowSpendToggle(isStoreValid(storeId, options, true));

        // Get the appropriate table ID based on toggle state
        const activeTableId = !spendToggleState ? tableId : localSpendTableId;

        // Fetch table data when branch is selected
        fetchTableOne({
          currentFilters: currentDateFilters,
          compareFilters: comparePeriodFilters,
          tableId: activeTableId,
          spendToggleState,
        });

        fetchTableTwo({
          currentFilters: currentYtdFilters,
          compareFilters: compareYtdFilters,
          tableId: activeTableId,
          spendToggleState,
        });

        // Only fetch table three if not Gale Partners (i.e., not ID 1) and not in sub-branch dashboard
        if (!storeId.includes(1) && !subBranchDashboard) {
          fetchTableThree({
            filters: currentYtdFilters,
            tableId: tableId,
            spendToggleState,
          });
        }
      }
      // For region-specific data
      else if (
        regionId &&
        (!storeId || storeId.length === 0) &&
        currentDateFiltersForRegion?.length > 0 &&
        comparePeriodFiltersForRegion?.length > 0 &&
        currentYtdFiltersForRegion?.length > 0 &&
        compareYtdFiltersForRegion?.length > 0
      ) {
        fetchRegionTableOne({
          currentFilters: currentDateFiltersForRegion,
          compareFilters: comparePeriodFiltersForRegion,
          tableId,
        });

        fetchRegionTableTwo({
          currentFilters: currentYtdFiltersForRegion,
          compareFilters: compareYtdFiltersForRegion,
          tableId,
        });
      }

      // Reset API call in progress flag
      apiCallInProgressRef.current = false;
    }, 50); // Small debounce to batch changes

    return () => {
      clearTimeout(debounceApiCall);
      apiCallInProgressRef.current = false;
    };
  }, [
    // Include all dependencies but NOT filtersHash state
    tableId,
    getUpdatedFiltersHash,
    currentDateFilters,
    comparePeriodFilters,
    currentYtdFilters,
    compareYtdFilters,
    storeId,
    options,
    spendToggleState,
    regionId,
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
    currentYtdFiltersForRegion,
    compareYtdFiltersForRegion,
    fetchTableOne,
    fetchTableTwo,
    fetchTableThree,
    fetchRegionTableOne,
    fetchRegionTableTwo,
    localSpendTableId,
  ]);

  const getDownloadPayloads = useMemo(() => {
    // Standard branch download filters
    const branchDownloadFilters = {
      menu_name: 'overall_performance_report',
      reporting_ytd_view_table_data: {
        current_query_builder: {
          filters: subBranchDashboard
            ? currentYtdFilters
            : removeFiltersByKeys(currentYtdFilters, ['store_id']),
        },
        previous_query_builder: {
          filters: subBranchDashboard
            ? compareYtdFilters
            : removeFiltersByKeys(compareYtdFilters, ['store_id']),
        },
        join_columns: ['store_id'],
      },
      reporting_compare_period_table_data: {
        current_query_builder: {
          filters: subBranchDashboard
            ? currentDateFilters
            : removeFiltersByKeys(currentDateFilters, ['store_id']),
        },
        previous_query_builder: {
          filters: subBranchDashboard
            ? comparePeriodFilters
            : removeFiltersByKeys(comparePeriodFilters, ['store_id']),
        },
        join_columns: ['store_id'],
      },
      ...(!subBranchDashboard && {
        overall_performance_region_query: {
          filters: removeFiltersByKeys(currentYtdFilters, ['store_id']),
        },
      }),
    };

    // Local spend filters version
    const localSpendFilters = {
      menu_name: 'overall_performance_report',
      reporting_local_ytd_view_table_data: {
        current_query_builder: {
          filters: subBranchDashboard
            ? currentYtdFilters
            : removeFiltersByKeys(currentYtdFilters, ['store_id']),
        },
        previous_query_builder: {
          filters: subBranchDashboard
            ? compareYtdFilters
            : removeFiltersByKeys(compareYtdFilters, ['store_id']),
          extras: {
            time_grain_sqla: 'P1D',
            having: '',
            where: 'fiscal_year>=2024',
          },
        },
        join_columns: ['store_id'],
      },
      reporting_local_compare_period_table_data: {
        current_query_builder: {
          filters: subBranchDashboard
            ? currentDateFilters
            : removeFiltersByKeys(currentDateFilters, ['store_id']),
        },
        previous_query_builder: {
          filters: subBranchDashboard
            ? comparePeriodFilters
            : removeFiltersByKeys(comparePeriodFilters, ['store_id']),
          extras: {
            time_grain_sqla: 'P1D',
            having: '',
            where: 'fiscal_year>=2024',
          },
        },
        join_columns: ['store_id'],
      },
      ...(!subBranchDashboard && {
        overall_performance_region_query_local_spend: {
          filters: removeFiltersByKeys(currentYtdFilters, ['store_id']),
        },
      }),
    };

    // Region download filters - use region filters if available, otherwise fall back to store filters
    const regionDownloadFilters = {
      menu_name: 'overall_performance_report',
      reporting_region_ytd_view_table_data: {
        current_query_builder: {
          filters: removeFiltersByKeys(
            currentYtdFiltersForRegion?.length > 0 ? currentYtdFiltersForRegion : currentYtdFilters,
            ['store_id', 'region_id']
          ),
        },
        previous_query_builder: {
          filters: removeFiltersByKeys(
            compareYtdFiltersForRegion?.length > 0 ? compareYtdFiltersForRegion : compareYtdFilters,
            ['store_id', 'region_id']
          ),
        },
        join_columns: ['region_id'],
      },
      reporting_region_compare_period_table_data: {
        current_query_builder: {
          filters: removeFiltersByKeys(
            currentDateFiltersForRegion?.length > 0
              ? currentDateFiltersForRegion
              : currentDateFilters,
            ['store_id', 'region_id']
          ),
        },
        previous_query_builder: {
          filters: removeFiltersByKeys(
            comparePeriodFiltersForRegion?.length > 0
              ? comparePeriodFiltersForRegion
              : comparePeriodFilters,
            ['store_id', 'region_id']
          ),
        },
        join_columns: ['region_id'],
      },
    };

    return {
      branchDownloadFilters,
      localSpendFilters,
      regionDownloadFilters,
    };
  }, [
    currentYtdFilters,
    compareYtdFilters,
    currentDateFilters,
    comparePeriodFilters,
    currentYtdFiltersForRegion,
    compareYtdFiltersForRegion,
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
  ]);

  return {
    // User data
    userData,

    // Filter state
    options,
    regionOptions,
    storeId,
    regionId,
    regionBranches,

    // UI state
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    defaultFilter,
    componentType,
    isAnyQueryLoading,
    resetRegion,
    resetStore,

    // Table data
    tableOneData,
    tableTwoData,
    tableThreeData,

    // Filter handlers
    handleStoreIdChange,
    handleRegionIdChange,
    handleResetForRegion,
    handleResetForStore,

    // Query states
    isTableOneLoading,
    isTableTwoLoading,
    isTableThreeLoading,
    tableOneError,
    tableTwoError,
    tableThreeError,

    // Table visibility conditions
    showTableThree,

    // Add these new properties
    branchDownloadFilters: getDownloadPayloads.branchDownloadFilters,
    localSpendFilters: getDownloadPayloads.localSpendFilters,
    regionDownloadFilters: getDownloadPayloads.regionDownloadFilters,
    showGraphs,
    oppsGraphProps,
    spendsGraphProps,
  };
}
